/*
 * Friendly branded offline state — the my.trumin REFERENCE implementation of the
 * programme UX standard (BOARDROOM-2026-08-12-120533, Duncan-directed): when a
 * session loses connectivity, every Trumin platform shows a warm, calm, on-brand
 * message instead of an ugly raw browser/error warning, and auto-recovers on
 * reconnect. my.trumin owns the LEAD — this component + OFFLINE_COPY are the
 * shared copy/treatment other surfaces (manage.trumin, box-office, App-1, RC)
 * mirror so the experience is identical everywhere.
 *
 * Portable by design: the only dependencies are the browser online/offline
 * events + navigator.onLine; the styling uses the shared brand tokens so any
 * Trumin app can drop it in. No raw error text is ever shown to the user.
 */
import { useEffect, useRef, useState } from 'react'

/** SHARED COPY — keep identical across all Trumin platforms (final copy is warm,
 *  calm, on-brand; never raw browser/error text). Proposed by my.trumin (lead);
 *  boardroom confirms + relays to the other surfaces. */
export const OFFLINE_COPY = {
  /** Shown while offline. */
  offline: "You're offline right now — we'll reconnect you automatically, and your progress is saved.",
  /** Brief confirmation flashed on reconnect, then auto-dismissed. */
  backOnline: 'Back online — you’re all set.',
} as const

/** `true` when the browser reports a live connection. Subscribes to the
 *  online/offline events; SSR/no-navigator environments default to online so we
 *  never show a false offline state. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' ? true : navigator.onLine,
  )
  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])
  return online
}

const wrap: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  display: 'flex',
  justifyContent: 'center',
  padding: '10px 16px',
  fontSize: 13.5,
  fontWeight: 600,
  textAlign: 'center',
  lineHeight: 1.4,
  transition: 'transform .25s ease, opacity .25s ease',
}

/**
 * Global offline banner. Renders nothing while online (after any brief
 * back-online confirmation clears). Mount ONCE near the app root.
 *
 * States: offline → warm amber "you're offline" banner (persists); on reconnect
 * → a teal "back online" confirmation for ~2.5s, then it dismisses itself.
 */
export function OfflineBanner() {
  const online = useOnlineStatus()
  const [showBackOnline, setShowBackOnline] = useState(false)
  // Previous online state as a ref so this effect depends ONLY on `online` — if
  // it also depended on a state we flip here, the re-run's cleanup would cancel
  // the dismiss timer before it fires.
  const prevOnline = useRef(online)

  useEffect(() => {
    const wasOnline = prevOnline.current
    prevOnline.current = online
    if (!online) {
      setShowBackOnline(false)
      return
    }
    if (!wasOnline) {
      // just reconnected — flash the confirmation, then auto-dismiss
      setShowBackOnline(true)
      const t = setTimeout(() => setShowBackOnline(false), 2500)
      return () => clearTimeout(t)
    }
  }, [online])

  if (online && !showBackOnline) return null

  const offlineStyle: React.CSSProperties = {
    ...wrap,
    background: 'var(--warn-bg, rgba(230,193,90,0.10))',
    color: 'var(--warn-ink, #141712)',
    borderBottom: '1px solid var(--warn, #e6c15a)',
  }
  const backOnlineStyle: React.CSSProperties = {
    ...wrap,
    background: 'rgba(63,224,200,0.12)',
    color: 'var(--text, #e6ecee)',
    borderBottom: '1px solid var(--accent-bright, #3fe0c8)',
  }

  return online ? (
    <div role="status" aria-live="polite" data-testid="offline-banner-online" style={backOnlineStyle}>
      {OFFLINE_COPY.backOnline}
    </div>
  ) : (
    <div role="status" aria-live="polite" data-testid="offline-banner-offline" style={offlineStyle}>
      {OFFLINE_COPY.offline}
    </div>
  )
}
